import { lazy, Suspense, useEffect } from "react"

import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router"

import { recordRoute } from "@/auth/lastRoute.tsx"
import { registerSessionExpiredHandler } from "@/authSession.ts"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import KPIAdmin from "@/pages/admin/KPIAdmin.tsx"
import LogisticsAdmin from "@/pages/admin/LogisticsAdmin.tsx"
import BCO from "@/pages/dashboards/BCO.tsx"
import DigesterMetrics from "@/pages/dashboards/DigesterMetrics.tsx"
import Digesters from "@/pages/dashboards/Digesters.tsx"
import FarmBalances from "@/pages/dashboards/FarmBalances.tsx"
import ForHire from "@/pages/dashboards/ForHire.tsx"
import ForHireBalanceMap from "@/pages/dashboards/ForHireBalanceMap.tsx"
import LWRN from "@/pages/dashboards/LWRN.tsx"
import PEPL from "@/pages/dashboards/PEPL.tsx"
import PLTH from "@/pages/dashboards/PLTH.tsx"
import SPL from "@/pages/dashboards/SPL.tsx"
import Definitions from "@/pages/datapoints/Definitions.tsx"
import EditDataPoint from "@/pages/datapoints/EditDataPoint.tsx"
import NewDataPoint from "@/pages/datapoints/NewDataPoint.tsx"
import EditReport from "@/pages/datapoints/reports/EditReport.tsx"
import NewReport from "@/pages/datapoints/reports/NewReport.tsx"
import ReportRuns from "@/pages/datapoints/reports/ReportRuns.tsx"
import Reports from "@/pages/datapoints/reports/Reports.tsx"
import Home from "@/pages/Home.tsx"
import Incentives from "@/pages/Incentives.tsx"
import KPIs from "@/pages/kpis/KPIs.tsx"
import { PermissionDenied } from "@/pages/PermissionDenied.tsx"
import { SessionExpiredPage } from "@/pages/SessionExpiredPage.tsx"

import AuthWrap from "./AuthWrap.tsx"
import BaseLayout from "./BaseLayout.tsx"
import { ThemeProvider } from "./ThemeProvider.tsx"

// Lazy: @scalar/api-reference-react roughly doubles the bundle (icon sets,
// an embedded chat component) and this page is admin-only -- no reason for
// every user to download it just to load the homepage.
const ApiDocs = lazy(() => import("@/pages/admin/ApiDocs.tsx"))

function AppRoutes() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Record the last good route on location change
    if (
      !location.pathname.startsWith("/session-expired") &&
      !location.pathname.startsWith("/permission-denied")
    ) {
      recordRoute(location.pathname)
    }
  }, [location])

  useEffect(() => {
    const unsubscribe = registerSessionExpiredHandler((returnTo) => {
      const safeReturnTo = returnTo && returnTo !== "/session-expired" ? returnTo : "/"

      navigate(`/session-expired?returnTo=${encodeURIComponent(safeReturnTo)}`)
    })

    // Cleanup so we don't keep a stale handler around
    return unsubscribe
  }, [navigate])

  return (
    <Routes>
      {/* All the "normal" app routes use BaseLayout */}
      <Route element={<BaseLayout />}>
        <Route index element={<Home />} />
        <Route path="dashboards">
          <Route path="BCO" element={<BCO />} />
          <Route path="Digesters">
            <Route index element={<Digesters />} />
            <Route path="Metrics" element={<DigesterMetrics />} />
          </Route>
          <Route path="SPL">
            <Route index element={<SPL />} />
            <Route path="Balances" element={<FarmBalances />} />
          </Route>
          <Route path="For-Hire">
            <Route index element={<ForHire />} />
            <Route path="balance" element={<ForHireBalanceMap />} />
          </Route>
          <Route path="NO" element={<Navigate to="/dashboards/SPL" replace />} />
          <Route path="PLTH" element={<PLTH />} />
          <Route path="LWRN" element={<LWRN />} />
          <Route path="PEPL" element={<PEPL />} />
        </Route>
        <Route path="kpis" element={<KPIs />} />
        <Route path="incentives" element={<Incentives />} />
        <Route path="datapoints">
          <Route path="report-runs" element={<ReportRuns />} />
          <Route path="reports">
            <Route index element={<Reports />} />
            <Route
              path="new"
              element={
                <AuthWrap role="Report.Admin" showMessage={true} redirectOnDenied={true}>
                  <NewReport />
                </AuthWrap>
              }
            />
            <Route
              path=":id"
              element={
                <AuthWrap role="Report.Admin" showMessage={true} redirectOnDenied={true}>
                  <EditReport />
                </AuthWrap>
              }
            />
          </Route>
          <Route path="defs">
            <Route
              index
              element={
                <AuthWrap role="Datapoint.Admin" showMessage={true} redirectOnDenied={true}>
                  <Definitions />
                </AuthWrap>
              }
            />
            <Route
              path="new/:type"
              element={
                <AuthWrap role="Datapoint.Admin" showMessage={true} redirectOnDenied={true}>
                  <NewDataPoint />
                </AuthWrap>
              }
            />
            <Route
              path=":id"
              element={
                <AuthWrap role="Datapoint.Admin" showMessage={true} redirectOnDenied={true}>
                  <EditDataPoint />
                </AuthWrap>
              }
            />
          </Route>
        </Route>
        <Route path="admin">
          <Route
            index
            element={
              <AuthWrap role="KPI.Admin" showMessage={true} redirectOnDenied={true}>
                <div className="p-4">
                  <h1 className="text-2xl font-bold mb-4">Administration</h1>
                  <p>Select an administrative function from the navigation.</p>
                </div>
              </AuthWrap>
            }
          />
          <Route
            path="kpi"
            element={
              <AuthWrap role="KPI.Admin" showMessage={true} redirectOnDenied={true}>
                <KPIAdmin />
              </AuthWrap>
            }
          />
          <Route
            path="logistics"
            element={
              <AuthWrap role="Logistics.Admin" showMessage={true} redirectOnDenied={true}>
                <LogisticsAdmin />
              </AuthWrap>
            }
          />
        </Route>
      </Route>

      {/* Session expired route WITHOUT BaseLayout (no nav/sidebar) */}
      <Route path="/session-expired" element={<SessionExpiredPage />} />

      {/* Permission denied route WITHOUT BaseLayout (no nav/sidebar) */}
      <Route path="/permission-denied" element={<PermissionDenied />} />

      {/* Scalar wants the full viewport, same as it gets natively on the
          backend's own /scalar route -- boxing it into BaseLayout's sidebar
          content area breaks its intended full-page layout. */}
      <Route
        path="/admin/api-docs"
        element={
          <AuthWrap role="Spec.Read" showMessage={true} redirectOnDenied={true}>
            <Suspense fallback={<Skeleton className="w-full h-full" />}>
              <ApiDocs />
            </Suspense>
          </AuthWrap>
        }
      />
    </Routes>
  )
}

export default function AppRouter() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  )
}
